import {
  DataGridPremiumProps,
  GridCallbackDetails,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridDensity,
  GridFilterModel,
  GridPinnedColumnFields,
  GridRowHeightParams,
  GridRowSelectionModel,
  GridRowsProp,
  GridSortModel,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import { FC, SetStateAction, Dispatch, useState, useMemo, useEffect, useCallback, JSX } from "react";
import TablePagination from "./TablePagination";
import * as S from "./styled";
import { cacheService } from "../../Core/Api/services";
import { customOperators } from "../../Utils/Helpers/customGridFilterOperators";
import { ClickAwayListener, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { GetLeftPanelInfos, GetRightPanelInfos } from "../../Redux/Reducers/redux-slice-layout";
import "../../Theme/CSS/table-focus-row.css";
import { useTableGrabber } from "../../Utils/Hooks/useTableGrabber";
import { useLocation } from "react-router";
import { CustomColumnsPanel, TableToolbar } from "./table-toolbar";
import { ExpandLessOutlined, ExpandMoreOutlined, UnfoldMoreOutlined } from "@mui/icons-material";
import { TableNoRows } from "./table-no-rows";
export interface DataTableProps extends DataGridPremiumProps {
  tableKey?: string;
  rows: GridRowsProp;
  selectedRows?: number[];
  pageSize?: number;
  setSelectedRows?: Dispatch<SetStateAction<number[]>>;
  initialColumnVisibility?: GridColumnVisibilityModel;
  overrideColumnVisibility?: GridColumnVisibilityModel;
  densityControl?: boolean;
  densityValue?: number;
  paginationServerSideChangeCallback?: (page: number, pageSize: number) => void;
  additionalFilters?: JSX.Element;
  additionalActions?: JSX.Element;
  noControls?: boolean;
}

export enum CellSize {
  xxsmall = 100,
  xsmall = 100,
  small = 100,
  medium = 150,
  large = 200,
  xlarge = 300,
  xxlarge = 500,
  // 1 action
  actionsXsmall = 50,
  // 2 actions
  actionsSmall = 90,
  // 3 actions
  actionsDefault = 130,
  // 4 actions
  actionsLarge = 170,
}

const DataTable: FC<DataTableProps> = (props) => {
  const {
    tableKey = "default",
    rows,
    columns,
    slots,
    selectedRows,
    pageSize,
    setSelectedRows,
    pagination = true,
    columnHeaderHeight = undefined,
    densityControl = undefined,
    getRowHeight = undefined,
    initialColumnVisibility,
    overrideColumnVisibility,
    checkboxSelection = false,
    paginationServerSideChangeCallback,
    additionalFilters,
    additionalActions,
    noControls = false,
    ...otherProps
  } = props;

  const tableRef = useTableGrabber();

  const handleSelectionModelChange = (newSelectedRows: GridRowSelectionModel) => {
    if (!setSelectedRows) return;
    setSelectedRows(newSelectedRows as number[]);
  };

  const [paginationModel, setPaginationModel] = useState({
    pageSize: pageSize ? pageSize : 20,
    page: 0,
  });

  //   const showColumnFilters = cacheService.GetActiveFeatures()?.find((x) => x === "Column Filters") ? true : false;
  const theme = useTheme();
  const leftPanel = useSelector(GetLeftPanelInfos);
  const rightPanel = useSelector(GetRightPanelInfos);
  const activeIds = [leftPanel.activeId, rightPanel.activeId];

  // Table cache setup
  const location = useLocation();

  const { columnFilters, columnsOrder, columnsVisibility, columnsSort, columnsPin } =
    cacheService.GetTableUserSetup(tableKey);

  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({
    ...initialColumnVisibility,
    ...columnsVisibility,
  });
  const [colsOrder, setColsOrder] = useState<{ [key: string]: number }>(
    columnsOrder ??
      [...columns].reduce((acc, col, index) => {
        acc[col.field] = index + 1;
        return acc;
      }, {})
  );
  const [sortModel, setSortModel] = useState(columnsSort);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [columnFiltersVisible, setColumnFiltersVisible] = useState<boolean>(columnFilters);
  const [pinnedColumnsModel, setPinnedColumnsModel] = useState<GridPinnedColumnFields>(
    columnsPin ?? {
      left: ["__check__"],
      right: ["driveActions", "actions"],
    }
  );

  const [densityModel, setDensityModel] = useState<GridDensity>(
    cacheService.GetDensity(location.pathname) ?? "standard"
  );
  const [orderedColumns, setOrderedColumns] = useState<GridColDef[]>(columns as GridColDef[]);

  //   const densityControlRowHeight = useCallback(
  //     ({ id, densityFactor }: GridRowHeightParams) => {
  //       if (densityFactor === 1.3) return "auto";
  //       return densityValue * (densityFactor - 0.05) * (densityFactor - 0.05);
  //     },
  //     [densityValue]
  //   );

  const defaultRowHeight: any = () => "auto";

  const densityControlRowHeight = useCallback(
    (params: GridRowHeightParams) => {
      if (densityModel === "standard") return "auto";
      if (densityModel === "comfortable") return "auto";
      if (densityModel === "compact") return 40;
      return "auto";
    },
    [densityModel]
  );

  const getRowHeightHandler = useMemo(
    () => getRowHeight ?? (densityControl ? densityControlRowHeight : defaultRowHeight),
    [densityControl, getRowHeight, densityControlRowHeight]
  );

  const handlePaginationChange = useCallback(
    (newPaginationModel) => {
      if (paginationServerSideChangeCallback)
        paginationServerSideChangeCallback(newPaginationModel.page, newPaginationModel.pageSize);
      setPaginationModel({ pageSize: newPaginationModel.pageSize, page: newPaginationModel.page });
    },
    [paginationServerSideChangeCallback, setPaginationModel]
  );

  const getDynamicValueOptions = useCallback(
    (rows: GridRowsProp, column: GridColDef): (string | number | boolean)[] => {
      const values: any[] = [];
      for (const row of rows) {
        let val;
        if (typeof column.valueGetter === "function") {
          //@ts-expect-error
          val = column.valueGetter(row[column.field], row, column.field, null);
        } else val = row[column.field];

        if (val) {
          if (Array.isArray(val)) {
            values.push(...val);
          } else {
            values.push(val);
          }
        }
      }

      const uniqueValues = new Set(
        values.filter((val): val is string | number | boolean => val !== undefined && val !== null)
      );

      return Array.from(uniqueValues).sort((a, b) =>
        typeof a === "string" && typeof b === "string" ? a.localeCompare(b) : 0
      );
    },
    [rows]
  );

  const replaceGridDefaultOperators = (columns: GridColDef[]) =>
    columns.map(
      (col) => {
        if (col.type === "singleSelect" && !col.filterOperators && props.paginationMode !== "server") {
          return {
            ...col,
            filterOperators: [customOperators.multiSelectOperator],
            valueOptions: getDynamicValueOptions(rows, col),
          };
        } else if (col.type === "string" && !col.filterOperators) {
          return {
            ...col,
            filterOperators: [customOperators.includesAllWords],
          };
        } else return col;
      }
      //   (!col.type || col.type === "string") && !col.filterOperators
      //     ? ({
      //         ...col,
      //         filterOperators: getGridStringOperators().map((x) =>
      //           x.value === "contains" ? customOperators.includesAllWords : x
      //         ),
      //         display: "flex",
      //         headerClassName: "MuiDataGrid-columnHeaders",
      //       } as any)
      //     : ({ ...col, display: "flex", headerClassName: "MuiDataGrid-columnHeaders" } as any)
    );

  // user setup and manipulations
  // column ordering : order columns prop depending on cache service orderedCols
  // We also update the ordered columns if columns are updated, manipulated from elsewhere (e.g New Request table)
  useEffect(() => {
    setOrderedColumns([...(columns as GridColDef[]).sort((a, b) => colsOrder[a.field] - colsOrder[b.field])]);
  }, [colsOrder, columns]);

  const handleColumnOrderChange = (
    params: GridColumnOrderChangeParams,
    event: MuiEvent<{}>,
    details: GridCallbackDetails
  ) => {
    const newOrder = { ...colsOrder };
    // Update the moved column's index
    newOrder[params.column.field] = params.targetIndex;

    // Adjust the indices of other columns
    Object.keys(newOrder).forEach((col) => {
      if (col !== params.column.field) {
        if (params.oldIndex < params.targetIndex) {
          // Moving forward: Shift columns between oldIndex and targetIndex backward
          if (newOrder[col] > params.oldIndex && newOrder[col] <= params.targetIndex) {
            newOrder[col]--;
          }
        } else {
          // Moving backward: Shift columns between targetIndex and oldIndex forward
          if (newOrder[col] < params.oldIndex && newOrder[col] >= params.targetIndex) {
            newOrder[col]++;
          }
        }
      }
    });
    setColsOrder(newOrder);
    cacheService.SetTableColumnOrder(tableKey, newOrder);
  };

  const handleColumnVisibilityChange = (visibilityModel: GridColumnVisibilityModel) => {
    setVisibilityModel(visibilityModel);
    cacheService.SetTableColumnVisibility(tableKey, visibilityModel);
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
    cacheService.SetTableSort(tableKey, sortModel);
  };

  const handleFilterModelChange = (filterModel: GridFilterModel) => {
    setFilterModel(filterModel);
  };

  const handlePinnedColumnsChange = (pinnedColumnsModel: GridPinnedColumnFields) => {
    setPinnedColumnsModel(pinnedColumnsModel);
    cacheService.SetTablePinnedColumns(tableKey, pinnedColumnsModel);
  };

  const handleColumnFiltersVisibleChange = (visible: boolean) => {
    setColumnFiltersVisible(visible);
    cacheService.SetTableColumnFilters(tableKey, visible);
  };

  return (
    <ClickAwayListener
      onClickAway={(e) => {
        const ev = e.target as any;
        const convertedEvent = ev as EventTarget & HTMLInputElement;
        convertedEvent?.focus();
      }}
    >
      <S.DataGrid
        noControls={noControls}
        ref={tableRef}
        onRowSelectionModelChange={handleSelectionModelChange}
        rowSelectionModel={selectedRows}
        rows={rows}
        // pagination
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pagination={pagination && !noControls}
        // user setup (cols order, sorting, visibility)
        columns={replaceGridDefaultOperators(orderedColumns)}
        onColumnOrderChange={handleColumnOrderChange}
        columnVisibilityModel={overrideColumnVisibility ?? visibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        ignoreDiacritics={true}
        pinnedColumns={pinnedColumnsModel}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        // Other props
        getRowClassName={(params) =>
          params.id === activeIds[0] || params.id === activeIds[1]
            ? "active-data-row"
            : params?.row?.isGroup
            ? `grouped-data-row-${theme.palette.mode}`
            : ""
        }
        columnHeaderHeight={48}
        slots={{
          ...slots,
          pagination: () =>
            !noControls &&
            TablePagination({
              densityControl: densityControl,
              serverSidePagination: paginationServerSideChangeCallback !== undefined,
            }),
          toolbar: !noControls ? TableToolbar : null,
          noRowsOverlay: TableNoRows,
          headerFilterMenu: null,
          columnsPanel: CustomColumnsPanel,
          columnSortedDescendingIcon: ExpandMoreOutlined,
          columnSortedAscendingIcon: ExpandLessOutlined,
          columnUnsortedIcon: UnfoldMoreOutlined,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            additionalFilters: additionalFilters,
            additionalActions: additionalActions,
            columnFiltersVisible: columnFiltersVisible,
            setColumnFiltersVisible: handleColumnFiltersVisibleChange,
            mandatoryColumnsVisibility: overrideColumnVisibility !== undefined,
          },
          headerFilterCell: {
            showClearIcon: false,
          },
          baseTextField: {
            variant: "outlined",
            label: null,
            placeholder: "Filter",
          },
          baseSelect: {
            variant: "outlined",
            label: null,
            displayEmpty: true,
          },
          baseFormControl: {
            variant: "outlined",
            hiddenLabel: true,
          },
          loadingOverlay: {
            variant: "skeleton",
            noRowsVariant: "skeleton",
          },
        }}
        hideFooter={noControls}
        disableColumnMenu
        disableColumnFilter
        headerFilters={!noControls && columnFiltersVisible}
        headerFilterHeight={32}
        getRowHeight={densityControlRowHeight}
        density={densityModel}
        onDensityChange={(density) => {
          setDensityModel(density);
          cacheService.SetDensity(density, location.pathname);
        }}
        checkboxSelection={checkboxSelection}
        {...otherProps}
      />
    </ClickAwayListener>
  );
};

export default DataTable;
